import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, X, MessageSquare, Bot } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { FloatingAIChat } from "./FloatingAIChat";
import { FloatingStaffChat } from "./FloatingStaffChat";

interface Conversation {
  id: string;
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
}

export function UnifiedFloatingChat() {
  const { user } = useAuth();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);

  // Fetch unread messages for staff conversations
  const { data: messages = [] } = useQuery({
    queryKey: ['floating-messages', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    refetchInterval: 10000,
  });

  // Group messages into conversations
  const conversations: Conversation[] = (() => {
    if (!user?.id || !messages.length) return [];
    
    const convMap = new Map<string, Conversation>();
    
    messages.forEach((msg: any) => {
      const isRecipient = msg.recipient_id === user.id;
      const otherUserId = isRecipient ? msg.sender_id : msg.recipient_id;
      const otherUserName = isRecipient ? msg.sender_name : msg.recipient_name;
      const otherUserEmail = isRecipient ? msg.sender_email : msg.recipient_email;
      
      if (!convMap.has(msg.conversation_id)) {
        convMap.set(msg.conversation_id, {
          id: msg.conversation_id,
          recipientId: otherUserId,
          recipientName: otherUserName,
          recipientEmail: otherUserEmail,
          lastMessage: msg.content || (msg.attachments?.length ? '📎 Attachment' : ''),
          lastMessageTime: msg.created_at,
          unreadCount: 0,
        });
      }
      
      if (isRecipient && !msg.read) {
        const conv = convMap.get(msg.conversation_id)!;
        conv.unreadCount++;
      }
    });
    
    return Array.from(convMap.values())
      .sort((a, b) => {
        // Sort by unread first, then by time
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
        return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
      })
      .slice(0, 5);
  })();

  const hasStaffChats = conversations.length > 0;
  const totalUnreadCount = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);

  // Click outside to close accordion
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setAccordionOpen(false);
      }
    };

    if (accordionOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [accordionOpen]);

  const handleMainButtonClick = () => {
    if (aiChatOpen || selectedConversation) {
      // Close any open chat
      setAiChatOpen(false);
      setSelectedConversation(null);
      setAccordionOpen(false);
    } else if (hasStaffChats) {
      // Toggle accordion
      setAccordionOpen(!accordionOpen);
    } else {
      // Open AI directly
      setAiChatOpen(true);
    }
  };

  const handleSelectAI = () => {
    setAccordionOpen(false);
    setSelectedConversation(null);
    setAiChatOpen(true);
  };

  const handleSelectConversation = (conv: Conversation) => {
    setAccordionOpen(false);
    setAiChatOpen(false);
    setSelectedConversation(conv);
  };

  const handleCloseChat = () => {
    setAiChatOpen(false);
    setSelectedConversation(null);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Don't render on the full messages page
  if (location.pathname === '/messages') {
    return null;
  }

  const isAnyChatOpen = aiChatOpen || selectedConversation;

  return (
    <div ref={containerRef} className="floating-chat-container fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50">
      {/* Chat Windows */}
      <AnimatePresence mode="wait">
        {aiChatOpen && (
          <FloatingAIChat key="ai-chat" onClose={handleCloseChat} />
        )}
        
        {selectedConversation && (
          <FloatingStaffChat 
            key="staff-chat"
            conversation={selectedConversation}
            onClose={handleCloseChat}
          />
        )}
      </AnimatePresence>

      {/* Accordion Selector */}
      <AnimatePresence>
        {accordionOpen && !isAnyChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-14 sm:bottom-20 right-0 w-64 sm:w-80"
          >
            <Card className="shadow-xl border-border/50 overflow-hidden">
              <ScrollArea className="max-h-72 sm:max-h-80">
                <div className="p-1.5 sm:p-2 space-y-0.5 sm:space-y-1">
                  {/* AI Assistant Option - Always First */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSelectAI}
                    className="w-full flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg hover:bg-primary/10 transition-colors text-left"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shrink-0">
                      <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs sm:text-sm">AI Assistant</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">Ask me anything</p>
                    </div>
                    <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground shrink-0" />
                  </motion.button>

                  {conversations.length > 0 && (
                    <div className="px-2 sm:px-3 py-1.5 sm:py-2">
                      <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Messages
                      </p>
                    </div>
                  )}

                  {/* Staff Conversations */}
                  {conversations.map((conv) => (
                    <motion.button
                      key={conv.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSelectConversation(conv)}
                      className="w-full flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg hover:bg-muted transition-colors text-left"
                    >
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                        <AvatarFallback className="bg-blue-500/10 text-blue-600 text-xs sm:text-sm">
                          {getInitials(conv.recipientName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <p className="font-medium text-xs sm:text-sm truncate">{conv.recipientName}</p>
                          <span className="text-[9px] sm:text-xs text-muted-foreground shrink-0">
                            {formatDistanceToNow(new Date(conv.lastMessageTime), { addSuffix: false })}
                          </span>
                        </div>
                        <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{conv.lastMessage}</p>
                      </div>
                      {conv.unreadCount > 0 && (
                        <Badge variant="destructive" className="h-4 sm:h-5 min-w-4 sm:min-w-5 flex items-center justify-center text-[9px] sm:text-xs shrink-0 px-1">
                          {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                        </Badge>
                      )}
                    </motion.button>
                  ))}

                  {/* View All Link */}
                  {hasStaffChats && (
                    <motion.a
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      href="/messages"
                      className="block w-full text-center py-1.5 sm:py-2 text-xs sm:text-sm text-primary hover:underline"
                    >
                      View all messages
                    </motion.a>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Floating Button */}
      <AnimatePresence>
        {!isAnyChatOpen && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <Button
              onClick={handleMainButtonClick}
              className={`
                relative w-11 h-11 sm:w-14 sm:h-14 rounded-full shadow-lg
                transition-all duration-200
                ${accordionOpen 
                  ? 'bg-muted hover:bg-muted/80 text-muted-foreground' 
                  : 'bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground hover:scale-110'
                }
              `}
              aria-label={accordionOpen ? "Close chat options" : "Open chat options"}
              title={hasStaffChats ? 'Open chat options' : 'Ask AI Assistant'}
            >
              <AnimatePresence mode="wait">
                {accordionOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <X className="h-4 w-4 sm:h-6 sm:w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="sparkles"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Sparkles className="h-4 w-4 sm:h-6 sm:w-6" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Unread Badge */}
              {totalUnreadCount > 0 && !accordionOpen && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 min-w-4 h-4 sm:min-w-5 sm:h-5 rounded-full bg-destructive text-destructive-foreground text-[9px] sm:text-xs flex items-center justify-center px-1 sm:px-1.5 font-medium"
                >
                  {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                </motion.div>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
